import type React from 'react';
import { createContext, useContext, useCallback, useMemo, useId } from 'react';
import {
  Toaster,
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  ToastIntent,
} from '@fluentui/react-components';

interface ToastOptions {
  title: string;
  body?: string;
  intent?: ToastIntent;
  timeout?: number;
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const toasterId = useId();
  const { dispatchToast } = useToastController(toasterId);

  const showToast = useCallback(
    (options: ToastOptions) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{options.title}</ToastTitle>
          {options.body && <ToastBody>{options.body}</ToastBody>}
        </Toast>,
        {
          intent: options.intent ?? 'info',
          timeout: options.timeout ?? 5000,
          position: 'top-end',
        }
      );
    },
    [dispatchToast]
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      <Toaster toasterId={toasterId} position="top-end" />
      {children}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
